import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Tag, TagVariant } from "component/shared/tag";
import { styles } from "./style";

export interface DescribedRadioOption {
	/** Handed back to onChange as-is, so a number stays a number. */
	value: string | number;
	label: string;
	description?: React.ReactNode;
	/** Small tag beside the label, e.g. "Recommended". Variant defaults to success. */
	tag?: {
		label: string;
		variant?: TagVariant;
	};
	disabled?: boolean;
	/** Shown before the label, e.g. a platform's mark. */
	icon?: React.ReactNode;
	/** Content directly under the option, e.g. a notice for it; the option's radio is described by it. */
	after?: React.ReactNode;
}

interface OwnProps {
	name: string;
	/** null or undefined leaves every option unselected. */
	value: string | number | null | undefined;
	options: DescribedRadioOption[];
	onChange: (value: string | number) => void;
	disabled?: boolean;
	"aria-labelledby"?: string;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

const Component: React.FC<Props> = ({
	classes = {},
	name,
	value,
	options,
	onChange,
	disabled,
	"aria-labelledby": ariaLabelledBy,
}) => {
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// RadioGroup reports a string; return the option's own value so numeric values survive.
		const option = options.find((option) => String(option.value) === event.target.value);
		if (option) {
			onChange(option.value);
		}
	};
	return (
		<Mui.RadioGroup
			name={name}
			value={value === null || value === undefined ? "" : String(value)}
			onChange={handleChange}
			aria-labelledby={ariaLabelledBy}
		>
			{options.map((option) => {
				const descriptionId = option.description ? `${name}-${option.value}-description` : undefined;
				const afterId = option.after ? `${name}-${option.value}-after` : undefined;
				const describedBy = [descriptionId, afterId].filter(Boolean).join(" ") || undefined;
				return (
					<React.Fragment key={String(option.value)}>
						<Mui.FormControlLabel
							value={String(option.value)}
							disabled={disabled || option.disabled}
							className={classes.option}
							control={
								<Mui.Radio
									className={classes.radio}
									inputProps={{ "aria-describedby": describedBy }}
								/>
							}
							label={
								<Mui.Box>
									<Mui.Box className={classes.titleRow}>
										{option.icon}
										<Mui.Typography className={classes.title}>{option.label}</Mui.Typography>
										{option.tag && (
											<Tag
												label={option.tag.label}
												variant={option.tag.variant ?? "success"}
												size="small"
											/>
										)}
									</Mui.Box>
									{option.description && (
										<Mui.Typography id={descriptionId} className={classes.description}>
											{option.description}
										</Mui.Typography>
									)}
								</Mui.Box>
							}
						/>
						{option.after && (
							<div id={afterId} className={classes.after}>
								{option.after}
							</div>
						)}
					</React.Fragment>
				);
			})}
		</Mui.RadioGroup>
	);
};

export const DescribedRadioGroup = withStyles(Component, styles);
